import axios from 'axios';

const apiClient = axios.create({
  baseURL: 'https://nexus-hr.talenthubupskilling.com/backend/hr-api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

function decodeToken(t: string | null): any {
  if (!t) return null;
  try {
    const part = t.indexOf('.') >= 0 ? t.split('.')[0] : t;
    let b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return JSON.parse(atob(b64));
  } catch { return null; }
}

apiClient.interceptors.request.use(
  (config) => {
    try {
      const t = localStorage.getItem('hr_access_token') || localStorage.getItem('hr_token') || localStorage.getItem('token');
      if (t) {
        const bearer = `Bearer ${t}`;
        const h: any = config.headers;
        if (h && typeof h.set === 'function') { h.set('Authorization', bearer); }
        else { config.headers = { ...(h || {}), Authorization: bearer } as any; }
      }
    } catch (e) { /* ignore */ }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const apiService = {
  getTenantId: () => {
  try {
    const storedTenantId = localStorage.getItem('hr_tenant_id');
    console.log('Stored Tenant ID:', storedTenantId);
    
    if (storedTenantId) return storedTenantId;

    const token = localStorage.getItem('hr_access_token');
    console.log('Token exists:', !!token);
    
    if (!token) {
      console.log('No token found, using default tenant');
      return 'nexus_hr_main';
    }
    
    const decoded = decodeToken(token) || {};
    console.log('Decoded token tenant_id:', decoded.tenant_id);
    
    return decoded.tenant_id || 'nexus_hr_main';
  } catch (error) {
    console.error('Tenant ID extraction error:', error);
    return 'nexus_hr_main';
  }
},
  getDashboardData: async () => {
    try {
      const [settingsRes, analyticsRes] = await Promise.all([
        apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=dashboard`),
        apiClient.get(`/analytics/analytics.php?tenant_id=${apiService.getTenantId()}&action=dashboard`),
      ]);
      const settings = settingsRes.data;
      const analytics = analyticsRes.data?.data || {};
      if (settings.data?.system) {
        settings.data.system = { ...settings.data.system, ...analytics };
      } else if (settings.data) {
        settings.data.system = { ...(settings.data.system || {}), ...analytics };
      }
      return settings;
    } catch (error) {
      console.error('Dashboard API error:', error);
      return { success: false, error: 'Failed to fetch dashboard data' };
    }
  },

  // Employee Management
  getEmployees: async () => {
    try {
      const response = await apiClient.get(`/employees/employees.php?tenant_id=${apiService.getTenantId()}&action=list`);
      return response.data;
    } catch (error) {
      console.error('Get employees error:', error);
      return { success: false, error: 'Failed to get employees' };
    }
  },

  createEmployee: async (employeeData: any) => {
    try {
      const response = await apiClient.post('/employees/employees.php', {
        ...employeeData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Create employee error:', error);
      return { success: false, error: 'Failed to create employee' };
    }
  },

  updateEmployee: async (id: string, employeeData: any) => {
    try {
      console.log('🔄 Updating employee:', id, employeeData);
      const requestData = {
        id: parseInt(id),
        ...employeeData
      };
      console.log('📤 Sending PUT request:', requestData);
      const response = await apiClient.put(`/employees/employees.php?tenant_id=${apiService.getTenantId()}`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('📥 PUT Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('💥 Update employee error:', error.response?.data || error.message);
      return { success: false, error: 'Failed to update employee' };
    }
  },

  deleteEmployee: async (id: string) => {
    try {
      console.log('🗑️ Deleting employee:', id);
      const response = await apiClient.delete(`/employees/employees.php?tenant_id=${apiService.getTenantId()}`, {
        data: { id: parseInt(id) },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('📥 DELETE Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('💥 Delete employee error:', error.response?.data || error.message);
      return { success: false, error: 'Failed to delete employee' };
    }
  },

  exportEmployees: async () => {
    try {
      const response = await apiClient.get(`/employees/employees.php?tenant_id=${apiService.getTenantId()}&action=export`);
      return response.data;
    } catch (error) {
      console.error('Export employees error:', error);
      return { success: false, error: 'Failed to export employees' };
    }
  },

  // Recruitment & Hiring
  getJobPostings: async () => {
    try {
      const response = await apiClient.get(`/recruitment/jobs.php?tenant_id=${apiService.getTenantId()}&action=list`);
      return response.data;
    } catch (error) {
      console.error('Get job postings error:', error);
      return { success: false, error: 'Failed to get job postings' };
    }
  },

  createJobPosting: async (jobData: any) => {
    try {
      const response = await apiClient.post('/recruitment/jobs.php', {
        ...jobData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Create job posting error:', error);
      return { success: false, error: 'Failed to create job posting' };
    }
  },

  updateJobPosting: async (jobId: string, jobData: any) => {
    try {
      const response = await apiClient.put('/recruitment/jobs.php', {
        ...jobData,
        id: jobId,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Update job posting error:', error);
      return { success: false, error: 'Failed to update job posting' };
    }
  },

  deleteJobPosting: async (jobId: string) => {
    try {
      const response = await apiClient.delete('/recruitment/jobs.php', {
        data: { id: jobId, tenant_id: apiService.getTenantId() }
      });
      return response.data;
    } catch (error) {
      console.error('Delete job posting error:', error);
      return { success: false, error: 'Failed to delete job posting' };
    }
  },

  getJobApplications: async () => {
    try {
      const response = await apiClient.get(`/recruitment/applications.php?tenant_id=${apiService.getTenantId()}&action=list`);
      return response.data;
    } catch (error) {
      console.error('Get job applications error:', error);
      return { success: false, error: 'Failed to get job applications' };
    }
  },

  updateApplicationStatus: async (id: number, status: string) => {
    try {
      const response = await apiClient.put(`/recruitment/applications.php?tenant_id=${apiService.getTenantId()}`, {
        id, status
      });
      return response.data;
    } catch (error) {
      console.error('Update application status error:', error);
      return { success: false, error: 'Failed to update application status' };
    }
  },

  // Onboarding
  getOnboardingSummary: async () => {
    try {
      const r = await apiClient.get(`/onboarding/onboarding.php?tenant_id=${apiService.getTenantId()}&action=summary`);
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  getOnboardingProcesses: async () => {
    try {
      const response = await apiClient.get(`/onboarding/onboarding.php?tenant_id=${apiService.getTenantId()}&action=list`);
      return response.data;
    } catch (error) {
      console.error('Get onboarding processes error:', error);
      return { success: false, error: 'Failed to get onboarding processes' };
    }
  },

  createOnboardingProcess: async (processData: any) => {
    try {
      const response = await apiClient.post('/onboarding/onboarding.php', {
        ...processData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Create onboarding process error:', error);
      return { success: false, error: 'Failed to create onboarding process' };
    }
  },

  updateOnboardingProcess: async (processId: string, processData: any) => {
    try {
      const response = await apiClient.put('/onboarding/onboarding.php', {
        ...processData,
        id: processId,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Update onboarding process error:', error);
      return { success: false, error: 'Failed to update onboarding process' };
    }
  },

  getOnboardingTasks: async (processId: string) => {
    try {
      const response = await apiClient.get(`/onboarding/onboarding.php?tenant_id=${apiService.getTenantId()}&process_id=${processId}`);
      return response.data;
    } catch (error) {
      console.error('Get onboarding tasks error:', error);
      return { success: false, error: 'Failed to get onboarding tasks' };
    }
  },

  updateOnboardingTask: async (taskId: string, taskData: any) => {
    try {
      const response = await apiClient.put('/onboarding/onboarding.php', {
        ...taskData,
        id: taskId,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Update onboarding task error:', error);
      return { success: false, error: 'Failed to update onboarding task' };
    }
  },

  deleteOnboarding: async (processId: string) => {
    try {
      const response = await apiClient.delete('/onboarding/onboarding.php', {
        data: {
          id: processId,
          tenant_id: apiService.getTenantId()
        }
      });
      return response.data;
    } catch (error) {
      console.error('Delete onboarding error:', error);
      return { success: false, error: 'Failed to delete onboarding process' };
    }
  },

  exportOnboarding: async () => {
    try {
      const response = await apiClient.get(`/onboarding/onboarding.php?tenant_id=${apiService.getTenantId()}&action=export`);
      return response.data;
    } catch (error) {
      console.error('Export onboarding error:', error);
      return { success: false, error: 'Failed to export onboarding data' };
    }
  },

  // Performance Management
  getPerformanceReviews: async () => {
    try {
      const response = await apiClient.get(`/performance/performance.php?tenant_id=${apiService.getTenantId()}&action=list`);
      return response.data;
    } catch (error) {
      console.error('Get performance reviews error:', error);
      return { success: false, error: 'Failed to get performance reviews' };
    }
  },

  createPerformanceReview: async (reviewData: any) => {
    try {
      const response = await apiClient.post('/performance/performance.php', {
        ...reviewData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Create performance review error:', error);
      return { success: false, error: 'Failed to create performance review' };
    }
  },

  updatePerformanceReview: async (reviewId: string, reviewData: any) => {
    try {
      const response = await apiClient.put('/performance/performance.php', {
        ...reviewData,
        id: reviewId,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Update performance review error:', error);
      return { success: false, error: 'Failed to update performance review' };
    }
  },

  deletePerformanceReview: async (reviewId: string) => {
    try {
      const response = await apiClient.delete('/performance/performance.php', {
        data: { id: reviewId, tenant_id: apiService.getTenantId() }
      });
      return response.data;
    } catch (error) {
      console.error('Delete performance review error:', error);
      return { success: false, error: 'Failed to delete performance review' };
    }
  },

  // Training & Development
  getTrainingPrograms: async () => {
    try {
      const response = await apiClient.get(`/training/training.php?tenant_id=${apiService.getTenantId()}&action=programs`);
      return response.data;
    } catch (error) {
      console.error('Get training programs error:', error);
      return { success: false, error: 'Failed to get training programs' };
    }
  },

  getTrainingEnrollments: async (programId?: string) => {
    try {
      const url = programId
        ? `/training/enrollments.php?tenant_id=${apiService.getTenantId()}&program_id=${programId}`
        : `/training/enrollments.php?tenant_id=${apiService.getTenantId()}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Get training enrollments error:', error);
      return { success: false, error: 'Failed to get training enrollments' };
    }
  },

  getTrainingDashboard: async () => {
    try {
      const response = await apiClient.get(`/training/training.php?tenant_id=${apiService.getTenantId()}&action=dashboard`);
      return response.data;
    } catch (error) {
      console.error('Get training dashboard error:', error);
      return { success: false, error: 'Failed to get training dashboard' };
    }
  },

  createTrainingProgram: async (programData: any) => {
    try {
      const response = await apiClient.post('/training/training.php', {
        ...programData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Create training program error:', error);
      return { success: false, error: 'Failed to create training program' };
    }
  },

  updateTrainingProgram: async (programId: string, programData: any) => {
    try {
      const response = await apiClient.put('/training/training.php', {
        ...programData,
        id: programId,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Update training program error:', error);
      return { success: false, error: 'Failed to update training program' };
    }
  },

  deleteTrainingProgram: async (programId: string) => {
    try {
      const response = await apiClient.delete('/training/training.php', {
        data: { id: programId, tenant_id: apiService.getTenantId() }
      });
      return response.data;
    } catch (error) {
      console.error('Delete training program error:', error);
      return { success: false, error: 'Failed to delete training program' };
    }
  },

  // Document Management
  getDocuments: async () => {
    try {
      const response = await apiClient.get(`/documents/documents.php?tenant_id=${apiService.getTenantId()}&action=list`);
      return response.data;
    } catch (error) {
      console.error('Get documents error:', error);
      return { success: false, error: 'Failed to fetch documents' };
    }
  },

  uploadDocument: async (formData: FormData) => {
    try {
      const file = formData.get('file');
      if (file) {
        formData.delete('file');
        formData.append('document_file', file);
      }
      
      const response = await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/documents/documents.php?action=upload&tenant_id=${apiService.getTenantId()}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData
      });
      return await response.json();
    } catch (error) {
      console.error('Upload error:', error);
      return { success: false, error: 'Upload failed' };
    }
  },

  downloadDocument: async (documentId: string) => {
    try {
      const response = await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/documents/documents.php?action=download&id=${documentId}&tenant_id=${apiService.getTenantId()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = contentDisposition 
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
          : `document_${documentId}`;
        
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        return { success: true, message: 'Document downloaded successfully' };
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      return { success: false, error: 'Download failed' };
    }
  },

  viewDocument: async (documentId: string) => {
    try {
      const url = `https://nexus-hr.talenthubupskilling.com/backend/hr-api/documents/documents.php?action=view&id=${documentId}&tenant_id=${apiService.getTenantId()}`;
      window.open(url, '_blank');
      return { success: true, message: 'Document opened in new tab' };
    } catch (error) {
      console.error('View document error:', error);
      return { success: false, error: 'Failed to view document' };
    }
  },

  deleteDocument: async (id: string) => {
    try {
      const response = await apiClient.delete(`/documents/documents.php?tenant_id=${apiService.getTenantId()}`, {
        data: { id: parseInt(id) }
      });
      return response.data;
    } catch (error) {
      console.error('Delete document error:', error);
      return { success: false, error: 'Failed to delete document' };
    }
  },

  // Leave Management
  getLeaveRequests: async (employeeId?: string) => {
    try {
      const params: any = { action: 'list', tenant_id: apiService.getTenantId() };
      if (employeeId) params.employee_id = employeeId;
      const response = await apiClient.get('/leave/requests.php', { params });
      return response.data;
    } catch (error) {
      console.error('Get leave requests error:', error);
      return { success: false, error: 'Failed to get leave requests' };
    }
  },

  createLeaveRequest: async (requestData: any) => {
    try {
      const response = await apiClient.post('/leave/requests.php', {
        action: 'create',
        ...requestData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Create leave request error:', error);
      return { success: false, error: 'Failed to create leave request' };
    }
  },

  updateLeaveRequest: async (requestId: string, requestData: any) => {
    try {
      const response = await apiClient.post('/leave/requests.php', {
        action: 'update',
        id: requestId,
        ...requestData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Update leave request error:', error);
      return { success: false, error: 'Failed to update leave request' };
    }
  },

  deleteLeaveRequest: async (requestId: string) => {
    try {
      const response = await apiClient.post('/leave/requests.php', {
        action: 'delete',
        request_id: requestId,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Delete leave request error:', error);
      return { success: false, error: 'Failed to delete leave request' };
    }
  },

  approveLeaveRequest: async (requestId: string, approvalData: any) => {
    try {
      const response = await apiClient.put('/leave/requests.php', {
        action: 'approve',
        request_id: requestId,
        approved_by: approvalData.approved_by,
        approved_date: approvalData.approved_date,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Approve leave request error:', error);
      return { success: false, error: 'Failed to approve leave request' };
    }
  },

  rejectLeaveRequest: async (requestId: string, rejectionData: any) => {
    try {
      const response = await apiClient.put('/leave/requests.php', {
        request_id: requestId,
        status: 'rejected',
        ...rejectionData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Reject leave request error:', error);
      return { success: false, error: 'Failed to reject leave request' };
    }
  },

  // Notifications
  getNotifications: async () => {
    try {
      const response = await apiClient.get(`/notifications/notifications.php?tenant_id=${apiService.getTenantId()}&action=list`);
      return response.data;
    } catch (error) {
      console.error('Get notifications error:', error);
      return { success: false, error: 'Failed to get notifications' };
    }
  },

  markNotificationAsRead: async (notificationId: string) => {
    try {
      const response = await apiClient.put('/notifications/notifications.php', {
        id: notificationId,
        status: 'read',
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Mark notification as read error:', error);
      return { success: false, error: 'Failed to mark notification as read' };
    }
  },

  // Settings & Administration API Methods
  getSettingsData: async () => {
    try {
      const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=dashboard`);
      return response.data;
    } catch (error) {
      console.error('Get settings data error:', error);
      return { success: false, error: 'Failed to get settings data' };
    }
  },

  // Organization Management
  
  organizationManagement: {
  getOrganizations: async () => {
    try {
      const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=organizations`);
      return response.data;
    } catch (error) {
      console.error('Get organizations error:', error);
      return { success: false, error: 'Failed to get organizations' };
    }
  },

  createOrganization: async (orgData: any) => {
    try {
      const response = await apiClient.post(`/settings/settings.php?tenant_id=${apiService.getTenantId()}`, {
        action: 'create_organization',
        ...orgData
      });
      return response.data;
    } catch (error) {
      console.error('Create organization error:', error);
      return { success: false, error: 'Failed to create organization' };
    }
  },

  updateOrganization: async (orgId: string, orgData: any) => {
    try {
      const response = await apiClient.put(`/settings/settings.php?tenant_id=${apiService.getTenantId()}`, {
        action: 'update_organization',
        id: orgId,
        ...orgData
      });
      return response.data;
    } catch (error) {
      console.error('Update organization error:', error);
      return { success: false, error: 'Failed to update organization' };
    }
  },

  viewOrganization: async (orgId: string) => {
    try {
      const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=view_organization&id=${orgId}`);
      return response.data;
    } catch (error) {
      console.error('View organization error:', error);
      return { success: false, error: 'Failed to view organization' };
    }
  },

  deleteOrganization: async (orgId: string) => {
    try {
      const response = await apiClient.delete(`/settings/settings.php?tenant_id=${apiService.getTenantId()}`, {
        data: {
          action: 'delete_organization',
          id: orgId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Delete organization error:', error);
      return { success: false, error: 'Failed to delete organization' };
    }
  }
},  

// Storage Management
getStorageUsage: async () => {
  try {
    const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=storage_usage`);
    return response.data;
  } catch (error) {
    console.error('Get storage usage error:', error);
    return { success: false, error: 'Failed to get storage usage' };
  }
},

calculateStorageUsage: async () => {
  try {
    const response = await apiClient.post(`/settings/settings.php?tenant_id=${apiService.getTenantId()}`, {
      action: 'calculate_storage'
    });
    return response.data;
  } catch (error) {
    console.error('Calculate storage error:', error);
    return { success: false, error: 'Failed to calculate storage usage' };
  }
},

requestMoreStorage: async (requestData: any) => {
  try {
    const response = await apiClient.post(`/settings/settings.php?tenant_id=${apiService.getTenantId()}`, {
      action: 'request_storage',
      ...requestData
    });
    return response.data;
  } catch (error) {
    console.error('Request storage error:', error);
    return { success: false, error: 'Failed to request more storage' };
  }
},

getStorageRequests: async () => {
  try {
    const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=storage_requests`);
    return response.data;
  } catch (error) {
    console.error('Get storage requests error:', error);
    return { success: false, error: 'Failed to get storage requests' };
  }
},

  // Public organization list for employee login (no authentication required)
getPublicOrganizations: async () => {
  try {
    const response = await apiClient.get('/settings/settings.php?action=public_organizations');
    return response.data;
  } catch (error) {
    console.error('Get public organizations error:', error);
    return { success: false, error: 'Failed to load organizations' };
  }
},

  // Subscription & Billing
  getSubscriptionPlans: async () => {
    try {
      const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=subscription_plans`);
      return response.data;
    } catch (error) {
      console.error('Get subscription plans error:', error);
      return { success: false, error: 'Failed to get subscription plans' };
    }
  },

  calculatePricing: async (employeeCount: number, billingCycle: string, currency: string) => {
    try {
      const response = await apiClient.post('/settings/settings.php', {
        action: 'calculate_pricing',
        employee_count: employeeCount,
        billing_cycle: billingCycle,
        currency: currency,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Calculate pricing error:', error);
      return { success: false, error: 'Failed to calculate pricing' };
    }
  },

  // Payment Integration (Secure)
  getPaymentConfig: async () => {
    try {
      const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=payment_config`);
      // Response will contain masked keys for display
      return response.data;
    } catch (error) {
      console.error('Get payment config error:', error);
      return { success: false, error: 'Failed to get payment configuration' };
    }
  },

  updatePaymentConfig: async (configData: any) => {
    try {
      const response = await apiClient.post('/settings/settings.php', {
        action: 'update_payment_config',
        ...configData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Update payment config error:', error);
      return { success: false, error: 'Failed to update payment configuration' };
    }
  },

  testPaystackConnection: async (testData: any) => {
    try {
      const response = await apiClient.post('/settings/settings.php', {
        action: 'test_paystack_connection',
        ...testData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Test Paystack connection error:', error);
      return { success: false, error: 'Failed to test Paystack connection' };
    }
  },

  // Language & Currency
  getLanguageCurrencySettings: async () => {
    try {
      const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=language_currency`);
      return response.data;
    } catch (error) {
      console.error('Get language currency settings error:', error);
      return { success: false, error: 'Failed to get language and currency settings' };
    }
  },

  updateLanguageCurrencySettings: async (settings: any) => {
    try {
      const response = await apiClient.post('/settings/settings.php', {
        action: 'update_language_currency',
        ...settings,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Update language currency settings error:', error);
      return { success: false, error: 'Failed to update language and currency settings' };
    }
  },

  // System Configuration
  getSystemConfig: async () => {
    try {
      const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=system_config`);
      return response.data;
    } catch (error) {
      console.error('Get system config error:', error);
      return { success: false, error: 'Failed to get system configuration' };
    }
  },

  updateSystemConfig: async (config: any) => {
    try {
      const response = await apiClient.post('/settings/settings.php', {
        action: 'update_system_config',
        ...config,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Update system config error:', error);
      return { success: false, error: 'Failed to update system configuration' };
    }
  },

  // Backup Management
  getBackups: async () => {
    try {
      const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=backups`);
      return response.data;
    } catch (error) {
      console.error('Get backups error:', error);
      return { success: false, error: 'Failed to get backups' };
    }
  },

  createBackup: async () => {
    try {
      const response = await apiClient.post('/settings/settings.php', {
        action: 'create_backup',
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Create backup error:', error);
      return { success: false, error: 'Failed to create backup' };
    }
  },

  // User & Role Management (Enhanced)
  getUsersWithRoles: async () => {
    try {
      const response = await apiClient.get(`/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=users_with_roles`);
      return response.data;
    } catch (error) {
      console.error('Get users with roles error:', error);
      return { success: false, error: 'Failed to get users and roles' };
    }
  },

  createUser: async (userData: any) => {
    try {
      const response = await apiClient.post('/settings/settings.php', {
        action: 'create_user',
        ...userData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Create user error:', error);
      return { success: false, error: 'Failed to create user' };
    }
  },

  updateUser: async (userId: string, userData: any) => {
    try {
      const response = await apiClient.put('/settings/settings.php', {
        action: 'update_user',
        id: userId,
        ...userData,
        tenant_id: apiService.getTenantId()
      });
      return response.data;
    } catch (error) {
      console.error('Update user error:', error);
      return { success: false, error: 'Failed to update user' };
    }
  },

  deleteUser: async (userId: string) => {
    try {
      const response = await apiClient.delete('/settings/settings.php', {
        data: {
          action: 'delete_user',
          id: userId,
          tenant_id: apiService.getTenantId()
        }
      });
      return response.data;
    } catch (error) {
      console.error('Delete user error:', error);
      return { success: false, error: 'Failed to delete user' };
    }
  },
// Payroll APIs - UPDATED
getPayrollRecords: async (params: any) => {
  try {
    const response = await apiClient.get('/payroll/payroll.php', {
      params: { 
        action: 'list',
        ...params, 
        tenant_id: apiService.getTenantId() 
      }
    });
    return response.data;
  } catch (error) {
    console.error('Payroll fetch error:', error);
    return { success: false, error: 'Failed to fetch payroll records' };
  }
},

getPayrollSummary: async (month: string) => {
  try {
    const response = await apiClient.get('/payroll/payroll.php', {
      params: { action: 'summary', month, tenant_id: apiService.getTenantId() }
    });
    return response.data;
  } catch (error) {
    console.error('Payroll summary error:', error);
    return { success: false, error: 'Failed to fetch payroll summary' };
  }
},

getDepartmentPayrollSummary: async (month: string) => {
  try {
    const response = await apiClient.get('/payroll/payroll.php', {
      params: { action: 'department_summary', month, tenant_id: apiService.getTenantId() }
    });
    return response.data;
  } catch (error) {
    console.error('Department payroll error:', error);
    return { success: false, error: 'Failed to fetch department payroll summary' };
  }
},

// Benefits APIs - UPDATED to use payroll.php endpoint
listBenefits: async () => {
  try {
    const response = await apiClient.get('/benefits/benefits.php', {
      params: { action: 'list', tenant_id: apiService.getTenantId() }
    });
    return response.data;
  } catch (error) {
    console.error('Benefits list error:', error);
    return { success: false, error: 'Failed to fetch benefits' };
  }
},

getBenefitsSummary: async () => {
  try {
    const response = await apiClient.get('/benefits/benefits.php', {
      params: { action: 'summary', tenant_id: apiService.getTenantId() }
    });
    return response.data;
  } catch (error) {
    console.error('Benefits summary error:', error);
    return { success: false, error: 'Failed to fetch benefits summary' };
  }
},

getBenefitsAnalytics: async () => {
  try {
    const response = await apiClient.get('/benefits/benefits.php', {
      params: { action: 'analytics', tenant_id: apiService.getTenantId() }
    });
    return response.data;
  } catch (error) {
    console.error('Benefits analytics error:', error);
    return { success: false, error: 'Failed to fetch benefits analytics' };
  }
},

deletePayrollRecord: async (id: number) => {
  try {
    const response = await apiClient.delete('/payroll/payroll.php', {
      data: { id, tenant_id: apiService.getTenantId() }
    });
    return response.data;
  } catch (error) {
    console.error('Delete payroll error:', error);
    return { success: false, error: 'Failed to delete payroll record' };
  }
},

createPayrollRecord: async (payrollData: any) => {
  try {
    const response = await apiClient.post('/payroll/payroll.php', {
      ...payrollData,
      tenant_id: apiService.getTenantId()
    });
    return response.data;
  } catch (error) {
    console.error('Create payroll error:', error);
    return { success: false, error: 'Failed to create payroll record' };
  }
},

updatePayrollRecord: async (id: number, payrollData: any) => {
  try {
    const response = await apiClient.put('/payroll/payroll.php', {
      ...payrollData,
      id,
      tenant_id: apiService.getTenantId()
    });
    return response.data;
  } catch (error) {
    console.error('Update payroll error:', error);
    return { success: false, error: 'Failed to update payroll record' };
  }
},

  calculatePayroll: async (basic: number, allowances = 0, overtime = 0, bonus = 0) => {
    try {
      const tenant_id = apiService.getTenantId();
      const response = await apiClient.get(`/payroll/payroll.php?action=calculate&tenant_id=${tenant_id}&basic_salary=${basic}&allowances=${allowances}&overtime_pay=${overtime}&bonus=${bonus}`);
      return response.data;
    } catch (error) {
      console.error('Calculate payroll error:', error);
      return { success: false, error: 'Calculation failed' };
    }
  },
deleteBenefit: async (id: number) => {
  try {
    const response = await apiClient.delete('/benefits/benefits.php', {
      data: { id, tenant_id: apiService.getTenantId() }
    });
    return response.data;
  } catch (error) {
    console.error('Delete benefit error:', error);
    return { success: false, error: 'Failed to delete benefit' };
  }
},

createBenefit: async (benefitData: any) => {
  try {
    const response = await apiClient.post('/benefits/benefits.php', {
      ...benefitData,
      tenant_id: apiService.getTenantId()
    });
    return response.data;
  } catch (error) {
    console.error('Create benefit error:', error);
    return { success: false, error: 'Failed to create benefit' };
  }
},

updateBenefit: async (id: number, benefitData: any) => {
  try {
    const response = await apiClient.put('/benefits/benefits.php', {
      ...benefitData,
      id,
      tenant_id: apiService.getTenantId()
    });
    return response.data;
  } catch (error) {
    console.error('Update benefit error:', error);
    return { success: false, error: 'Failed to update benefit' };
  }
},

  // Analytics APIs
  getDashboardKPIs: async () => {
    try {
      const response = await apiClient.get('/analytics/analytics.php', {
        params: { action: 'dashboard', tenant_id: apiService.getTenantId() }
      });
      return response.data;
    } catch (error) {
      console.error('Dashboard KPI error:', error);
      return { success: false, error: 'Failed to fetch dashboard KPIs' };
    }
  },

  getPayrollTrends: async () => {
    try {
      const response = await apiClient.get('/analytics/analytics.php', {
        params: { action: 'payroll_trends', tenant_id: apiService.getTenantId() }
      });
      return response.data;
    } catch (error) {
      console.error('Payroll trends error:', error);
      return { success: false, error: 'Failed to fetch payroll trends' };
    }
  },

  getDepartmentBreakdown: async () => {
    try {
      const response = await apiClient.get('/analytics/analytics.php', {
        params: { action: 'department_breakdown', tenant_id: apiService.getTenantId() }
      });
      return response.data;
    } catch (error) {
      console.error('Department breakdown error:', error);
      return { success: false, error: 'Failed to fetch department breakdown' };
    }
  },

  getEmployeeDistribution: async () => {
    try {
      const response = await apiClient.get('/analytics/analytics.php', {
        params: { action: 'employee_distribution', tenant_id: apiService.getTenantId() }
      });
      return response.data;
    } catch (error) {
      console.error('Employee distribution error:', error);
      return { success: false, error: 'Failed to fetch employee distribution' };
    }
  },

  getSalaryAnalysis: async () => {
    try {
      const response = await apiClient.get('/analytics/analytics.php', {
        params: { action: 'salary_analysis', tenant_id: apiService.getTenantId() }
      });
      return response.data;
    } catch (error) {
      console.error('Salary analysis error:', error);
      return { success: false, error: 'Failed to fetch salary analysis' };
    }
  },
getAnalyticsDashboard: async () => {
  try {
    const response = await apiClient.get('/analytics/analytics.php', {
      params: { action: 'dashboard', tenant_id: apiService.getTenantId() }
    });
    return response.data;
  } catch (error) {
    console.error('Analytics dashboard error:', error);
    return { success: false, error: 'Failed to fetch analytics dashboard' };
  }
},

getDepartmentAnalysis: async () => {
  try {
    const response = await apiClient.get('/analytics/analytics.php', {
      params: { action: 'department_breakdown', tenant_id: apiService.getTenantId() }
    });
    return response.data;
  } catch (error) {
    console.error('Department analysis error:', error);
    return { success: false, error: 'Failed to fetch department analysis' };
  }
},

  // ── Kirkpatrick Training Evaluation ──────────────────────────────────────
  setEmployeePassword: async (employeeId: number, password: string, isTemp: boolean = true) => {
    try {
      const r = await apiClient.put(`/employees/employees.php?action=set_password&tenant_id=${apiService.getTenantId()}`, {
        employee_id: employeeId, password, is_temp: isTemp, tenant_id: apiService.getTenantId()
      });
      return r.data;
    } catch { return { success: false, error: 'Failed to set password' }; }
  },
  getKirkpatrickSummary: async (programId: string) => {
    try {
      const r = await apiClient.get(`/training/kirkpatrick.php?action=summary&tenant_id=${apiService.getTenantId()}&program_id=${programId}`);
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  getKirkpatrickLevel: async (level: number, programId: string) => {
    try {
      const r = await apiClient.get(`/training/kirkpatrick.php?level=${level}&tenant_id=${apiService.getTenantId()}&program_id=${programId}`);
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  saveKirkpatrickLevel: async (level: number, data: any) => {
    try {
      const r = await apiClient.post(`/training/kirkpatrick.php?level=${level}&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() });
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Buddy / Mentorship ────────────────────────────────────────────────────
  getBuddyAssignments: async () => {
    try {
      const r = await apiClient.get(`/onboarding/buddy.php?action=list&tenant_id=${apiService.getTenantId()}`);
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  getBuddySummary: async () => {
    try {
      const r = await apiClient.get(`/onboarding/buddy.php?action=summary&tenant_id=${apiService.getTenantId()}`);
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  getEligibleBuddies: async () => {
    try {
      const r = await apiClient.get(`/onboarding/buddy.php?action=eligible_buddies&tenant_id=${apiService.getTenantId()}`);
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  createBuddyAssignment: async (data: any) => {
    try {
      const r = await apiClient.post(`/onboarding/buddy.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() });
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  updateBuddyAssignment: async (id: number, data: any) => {
    try {
      const r = await apiClient.put(`/onboarding/buddy.php?tenant_id=${apiService.getTenantId()}`, { ...data, id, tenant_id: apiService.getTenantId() });
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  getBuddyFeedback: async (assignmentId: number) => {
    try {
      const r = await apiClient.get(`/onboarding/buddy.php?action=feedback&assignment_id=${assignmentId}&tenant_id=${apiService.getTenantId()}`);
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  submitBuddyFeedback: async (data: any) => {
    try {
      const r = await apiClient.post(`/onboarding/buddy.php?action=feedback&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() });
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  // ── Employee Engagement ───────────────────────────────────────────────────
  getEngagementSurveys: async () => {
    try { const r = await apiClient.get(`/engagement/engagement.php?type=survey&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  createSurvey: async (data: any) => {
    try { const r = await apiClient.post(`/engagement/engagement.php?type=survey&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  updateSurvey: async (data: any) => {
    try { const r = await apiClient.put(`/engagement/engagement.php?type=survey&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  submitSurveyResponse: async (data: any) => {
    try { const r = await apiClient.post(`/engagement/engagement.php?type=response&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getRecognitions: async () => {
    try { const r = await apiClient.get(`/engagement/engagement.php?type=recognition&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  sendRecognition: async (data: any) => {
    try { const r = await apiClient.post(`/engagement/engagement.php?type=recognition&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getMoodCheckins: async (period?: string) => {
    try { const r = await apiClient.get(`/engagement/engagement.php?type=mood&tenant_id=${apiService.getTenantId()}${period ? '&period='+period : ''}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  submitMoodCheckin: async (data: any) => {
    try { const r = await apiClient.post(`/engagement/engagement.php?type=mood&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getEngagementSummary: async () => {
    try { const r = await apiClient.get(`/engagement/engagement.php?type=survey&action=summary&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Compensation Management ───────────────────────────────────────────────
  getSalaryGrades: async () => {
    try { const r = await apiClient.get(`/compensation/compensation.php?type=grades&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  createSalaryGrade: async (data: any) => {
    try { const r = await apiClient.post(`/compensation/compensation.php?type=grades&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  updateSalaryGrade: async (data: any) => {
    try { const r = await apiClient.put(`/compensation/compensation.php?type=grades&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getJobEvaluations: async () => {
    try { const r = await apiClient.get(`/compensation/compensation.php?type=evaluation&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  createJobEvaluation: async (data: any) => {
    try { const r = await apiClient.post(`/compensation/compensation.php?type=evaluation&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Succession Planning ───────────────────────────────────────────────────
  getTalentPool: async () => {
    try { const r = await apiClient.get(`/succession/succession.php?type=talent&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  addToTalentPool: async (data: any) => {
    try { const r = await apiClient.post(`/succession/succession.php?type=talent&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  updateTalentPool: async (data: any) => {
    try { const r = await apiClient.put(`/succession/succession.php?type=talent&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getSuccessionPlans: async () => {
    try { const r = await apiClient.get(`/succession/succession.php?type=plans&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  createSuccessionPlan: async (data: any) => {
    try { const r = await apiClient.post(`/succession/succession.php?type=plans&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getSuccessionSummary: async () => {
    try { const r = await apiClient.get(`/succession/succession.php?type=summary&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── HR Helpdesk ───────────────────────────────────────────────────────────
  getHelpdeskTickets: async (status?: string, employeeId?: number) => {
    try {
      let url = `/helpdesk/helpdesk.php?tenant_id=${apiService.getTenantId()}`;
      if (status) url += `&status=${status}`;
      if (employeeId) url += `&employee_id=${employeeId}`;
      const r = await apiClient.get(url); return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  createHelpdeskTicket: async (data: any) => {
    try { const r = await apiClient.post(`/helpdesk/helpdesk.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  updateHelpdeskTicket: async (data: any) => {
    try { const r = await apiClient.put(`/helpdesk/helpdesk.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getTicketComments: async (ticketId: string) => {
    try { const r = await apiClient.get(`/helpdesk/helpdesk.php?action=comments&ticket_id=${ticketId}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  addTicketComment: async (data: any) => {
    try { const r = await apiClient.post(`/helpdesk/helpdesk.php?action=comment&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getHelpdeskSummary: async () => {
    try { const r = await apiClient.get(`/helpdesk/helpdesk.php?action=summary&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Statutory Remittances ─────────────────────────────────────────────────
  getStatutoryRemittances: async (type?: string) => {
    try { const r = await apiClient.get(`/statutory/statutory.php?tenant_id=${apiService.getTenantId()}${type ? '&type='+type : ''}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  generateRemittanceSchedule: async (period: string) => {
    try { const r = await apiClient.get(`/statutory/statutory.php?action=generate&period=${period}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  createRemittance: async (data: any) => {
    try { const r = await apiClient.post(`/statutory/statutory.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  updateRemittance: async (data: any) => {
    try { const r = await apiClient.put(`/statutory/statutory.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getStatutorySummary: async () => {
    try { const r = await apiClient.get(`/statutory/statutory.php?action=summary&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Training Evaluations ────────────────────────────────────────────────────
  getKirkpatrickHRDashboard: async (programId?: string) => {
    try { const r = await apiClient.get(`/training/kirkpatrick.php?action=hr_dashboard&tenant_id=${apiService.getTenantId()}${programId?'&program_id='+programId:''}`); return r.data; } catch { return { success: false }; }
  },
  getMyEvaluations: async (employeeId: number, programId?: string) => {
    try { const r = await apiClient.get(`/training/kirkpatrick.php?action=my_evaluations&employee_id=${employeeId}&tenant_id=${apiService.getTenantId()}${programId?'&program_id='+programId:''}`); return r.data; } catch { return { success: false }; }
  },
  shareEvaluation: async (data: any) => {
    try { const r = await apiClient.post(`/training/kirkpatrick.php?action=share&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },

  // ── Nexus BMP Sync ───────────────────────────────────────────────────────────
  getNexusBMPStatus: async () => {
    try { const r = await apiClient.get(`/integrations/nexus_bmp_sync.php?action=status&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  syncEmployeesToBMP: async () => {
    try { const r = await apiClient.post(`/integrations/nexus_bmp_sync.php?action=sync_employees&tenant_id=${apiService.getTenantId()}`, { tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  syncPayrollToBMP: async (period: string) => {
    try { const r = await apiClient.post(`/integrations/nexus_bmp_sync.php?action=sync_payroll&tenant_id=${apiService.getTenantId()}`, { period, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  syncLeaveToBMP: async (month: string) => {
    try { const r = await apiClient.post(`/integrations/nexus_bmp_sync.php?action=sync_leave&tenant_id=${apiService.getTenantId()}`, { month, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  getBMPJournalEntries: async () => {
    try { const r = await apiClient.get(`/integrations/nexus_bmp_sync.php?action=get_journals&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },

  // ── Integrations ──────────────────────────────────────────────────────────
  getIntegrationsCatalogue: async () => {
    try { const r = await apiClient.get(`/integrations/integrations.php?action=catalogue&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  saveIntegration: async (data: any) => {
    try { const r = await apiClient.post(`/integrations/integrations.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  testIntegration: async (integrationType: string) => {
    try { const r = await apiClient.post(`/integrations/integrations.php?action=test&tenant_id=${apiService.getTenantId()}`, { integration_type: integrationType, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Employee Self-Service ─────────────────────────────────────────────────
  getMyPayslips: async (employeeId: number) => {
    try { const r = await apiClient.get(`/payroll/payroll.php?action=list&tenant_id=${apiService.getTenantId()}&employee_id=${employeeId}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getMyLeaveBalance: async (employeeId: number) => {
    try { const r = await apiClient.get(`/leave/leave.php?action=balance&tenant_id=${apiService.getTenantId()}&employee_id=${employeeId}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Leave Management (full) ───────────────────────────────────────────────
  getLeaveBalance: async (employeeId?: number) => {
    try {
      const url = employeeId
        ? `/leave/requests.php?action=balance&tenant_id=${apiService.getTenantId()}&employee_id=${employeeId}`
        : `/leave/requests.php?action=balance&tenant_id=${apiService.getTenantId()}`;
      const r = await apiClient.get(url); return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  getLeaveCalendar: async (month: string) => {
    try { const r = await apiClient.get(`/leave/requests.php?action=calendar&month=${month}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getPublicHolidays: async (year?: string) => {
    try { const r = await apiClient.get(`/leave/requests.php?action=holidays&year=${year||new Date().getFullYear()}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getLeaveEntitlements: async () => {
    try { const r = await apiClient.get(`/leave/requests.php?action=entitlements&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  updateLeaveEntitlement: async (data: any) => {
    try { const r = await apiClient.put(`/leave/requests.php?action=entitlement&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  addPublicHoliday: async (data: any) => {
    try { const r = await apiClient.post(`/leave/requests.php?action=holiday&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Payroll Workflow ──────────────────────────────────────────────────────
  approvePayroll: async (id: number, approvedBy: number) => {
    try { const r = await apiClient.put(`/payroll/payroll.php?action=approve&tenant_id=${apiService.getTenantId()}`, { id, approved_by: approvedBy, tenant_id: apiService.getTenantId(), workflow_action: 'approve' }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  markPayrollPaid: async (id: number) => {
    try { const r = await apiClient.put(`/payroll/payroll.php?action=mark_paid&tenant_id=${apiService.getTenantId()}`, { id, tenant_id: apiService.getTenantId(), workflow_action: 'mark_paid' }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  rejectPayroll: async (id: number, reason: string) => {
    try { const r = await apiClient.put(`/payroll/payroll.php?action=reject&tenant_id=${apiService.getTenantId()}`, { id, rejection_reason: reason, tenant_id: apiService.getTenantId(), workflow_action: 'reject' }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  bulkApprovePayroll: async (ids: number[], approvedBy: number) => {
    try { const r = await apiClient.put(`/payroll/payroll.php?action=bulk_approve&tenant_id=${apiService.getTenantId()}`, { ids, approved_by: approvedBy, tenant_id: apiService.getTenantId(), workflow_action: 'bulk_approve' }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Performance — KPI/OKR ─────────────────────────────────────────────────
  getKPIs: async (employeeId?: number) => {
    try {
      const url = `/performance/performance.php?type=kpi&tenant_id=${apiService.getTenantId()}${employeeId ? '&employee_id='+employeeId : ''}`;
      const r = await apiClient.get(url); return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  createKPI: async (data: any) => {
    try { const r = await apiClient.post(`/performance/performance.php?type=kpi&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  updateKPI: async (data: any) => {
    try { const r = await apiClient.put(`/performance/performance.php?type=kpi&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Performance — 360 Feedback ────────────────────────────────────────────
  get360Feedback: async (subjectId?: number) => {
    try {
      const url = `/performance/performance.php?type=360&tenant_id=${apiService.getTenantId()}${subjectId ? '&subject_id='+subjectId : ''}`;
      const r = await apiClient.get(url); return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  submit360Feedback: async (data: any) => {
    try { const r = await apiClient.post(`/performance/performance.php?type=360&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Performance — PIP ─────────────────────────────────────────────────────
  getPIPs: async (employeeId?: number) => {
    try {
      const url = `/performance/performance.php?type=pip&tenant_id=${apiService.getTenantId()}${employeeId ? '&employee_id='+employeeId : ''}`;
      const r = await apiClient.get(url); return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  createPIP: async (data: any) => {
    try { const r = await apiClient.post(`/performance/performance.php?type=pip&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  updatePIP: async (data: any) => {
    try { const r = await apiClient.put(`/performance/performance.php?type=pip&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Reports ───────────────────────────────────────────────────────────────
  getReport: async (report: string, period?: string, year?: string) => {
    try {
      const url = `/reports/reports.php?report=${report}&tenant_id=${apiService.getTenantId()}${period?'&period='+period:''}${year?'&year='+year:''}`;
      const r = await apiClient.get(url); return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  getReportsDashboard: async () => {
    try { const r = await apiClient.get(`/reports/reports.php?report=summary&tenant_id=${apiService.getTenantId()}&period=${new Date().toISOString().slice(0,7)}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Payslip PDF ───────────────────────────────────────────────────────────
  downloadPayslip: async (payrollId: number) => {
    try {
      const url = `/payroll/payslip.php?id=${payrollId}&tenant_id=${apiService.getTenantId()}&format=pdf`;
      const response = await apiClient.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Payslip_${payrollId}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      return { success: true };
    } catch { return { success: false, error: 'Failed to download payslip' }; }
  },

  // ── Benefits (full) ───────────────────────────────────────────────────────
  enrollEmployeeInBenefit: async (data: any) => {
    try { const r = await apiClient.post(`/benefits/benefits.php?tenant_id=${apiService.getTenantId()}`, { ...data, action: 'enroll', tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  enrollInBenefit: async (data: any) => {
    try { const r = await apiClient.post(`/benefits/benefits.php?tenant_id=${apiService.getTenantId()}`, { ...data, action: 'enroll', tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getBenefitEnrollments: async (benefitId?: number) => {
    try {
      const url = `/benefits/benefits.php?action=enrollments&tenant_id=${apiService.getTenantId()}${benefitId ? '&benefit_id='+benefitId : ''}`;
      const r = await apiClient.get(url); return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  getEmployeeBenefits: async (employeeId: number) => {
    try { const r = await apiClient.get(`/benefits/benefits.php?action=employee_benefits&employee_id=${employeeId}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getBenefitCostReport: async () => {
    try { const r = await apiClient.get(`/benefits/benefits.php?action=cost_report&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getHMOProviders: async () => {
    try { const r = await apiClient.get(`/benefits/benefits.php?action=hmo_providers&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  unenrolEmployeeFromBenefit: async (employeeId: number, benefitId: number) => {
    try { const r = await apiClient.post(`/benefits/benefits.php?action=unenrol&tenant_id=${apiService.getTenantId()}`, { employee_id: employeeId, benefit_id: benefitId, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Documents (expiry + versions) ─────────────────────────────────────────
  getDocumentExpiryAlerts: async (days: number = 30) => {
    try { const r = await apiClient.get(`/documents/documents.php?action=expiry_alerts&days=${days}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getDocumentVersions: async (documentId: number) => {
    try { const r = await apiClient.get(`/documents/documents.php?action=versions&document_id=${documentId}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Performance — Competency Framework ───────────────────────────────────
  getCompetencies: async (employeeId?: number) => {
    try {
      const url = `/performance/performance.php?type=competency&tenant_id=${apiService.getTenantId()}${employeeId ? '&employee_id='+employeeId : ''}`;
      const r = await apiClient.get(url); return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },
  saveCompetency: async (data: any) => {
    try { const r = await apiClient.post(`/performance/performance.php?type=competency&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Bank Transfer (Paystack) ──────────────────────────────────────────────
  getBanks: async () => {
    try { const r = await apiClient.get(`/payroll/bank_transfer.php?action=banks&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { status: false }; }
  },
  verifyBankAccount: async (accountNumber: string, bankCode: string) => {
    try { const r = await apiClient.get(`/payroll/bank_transfer.php?action=verify_account&account_number=${accountNumber}&bank_code=${bankCode}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { status: false }; }
  },
  createTransferRecipient: async (data: any) => {
    try { const r = await apiClient.post(`/payroll/bank_transfer.php?action=create_recipient&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { status: false }; }
  },
  initiateTransfer: async (payrollId: number | string) => {
    try { const r = await apiClient.post(`/payroll/bank_transfer.php?action=initiate&tenant_id=${apiService.getTenantId()}`, { payroll_id: payrollId, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  initiateBulkTransfer: async (period: string) => {
    try { const r = await apiClient.post(`/payroll/bank_transfer.php?action=bulk_transfer&tenant_id=${apiService.getTenantId()}`, { period, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  getTransferHistory: async () => {
    try { const r = await apiClient.get(`/payroll/bank_transfer.php?action=transfers&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Disciplinary & Grievance ──────────────────────────────────────────────
  getDisciplinaryCases: async (type: string = 'disciplinary') => {
    try { const r = await apiClient.get(`/disciplinary/disciplinary.php?type=${type}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  getDisciplinarySummary: async (type: string = 'disciplinary') => {
    try { const r = await apiClient.get(`/disciplinary/disciplinary.php?type=${type}&action=summary&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  createDisciplinaryCase: async (data: any) => {
    try { const r = await apiClient.post(`/disciplinary/disciplinary.php?type=${data.type||'disciplinary'}&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  updateDisciplinaryCase: async (data: any) => {
    try { const r = await apiClient.put(`/disciplinary/disciplinary.php?type=${data.type||'disciplinary'}&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },

  // ── Exit Management ───────────────────────────────────────────────────────
  getExits: async () => {
    try { const r = await apiClient.get(`/exit/exit.php?tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  getExitSummary: async () => {
    try { const r = await apiClient.get(`/exit/exit.php?action=summary&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  initiateExit: async (data: any) => {
    try { const r = await apiClient.post(`/exit/exit.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  updateExit: async (data: any) => {
    try { const r = await apiClient.put(`/exit/exit.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },

  // ── Attendance & Shifts ───────────────────────────────────────────────────
  getAttendance: async (period?: string, employeeId?: number) => {
    try {
      let url = `/attendance/attendance.php?tenant_id=${apiService.getTenantId()}`;
      if (period) url += `&period=${period}`;
      if (employeeId) url += `&employee_id=${employeeId}`;
      const r = await apiClient.get(url); return r.data;
    } catch { return { success: false }; }
  },
  getAttendanceSummary: async (period?: string) => {
    try { const r = await apiClient.get(`/attendance/attendance.php?action=summary&period=${period||''}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  clockIn: async (employeeId: number, method: string = 'web', coords?: { latitude?: number; longitude?: number }) => {
    try { const r = await apiClient.post(`/attendance/attendance.php?action=clockin&tenant_id=${apiService.getTenantId()}`, { employee_id: employeeId, method, latitude: coords?.latitude, longitude: coords?.longitude, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  recordAttendance: async (data: any) => {
    try { const r = await apiClient.post(`/attendance/attendance.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  getShifts: async () => {
    try { const r = await apiClient.get(`/attendance/attendance.php?type=shifts&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  createShift: async (data: any) => {
    try { const r = await apiClient.post(`/attendance/attendance.php?type=shifts&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  assignShift: async (employeeId: number, shiftId: number) => {
    try { const r = await apiClient.put(`/attendance/attendance.php?action=assign_shift&tenant_id=${apiService.getTenantId()}`, { employee_id: employeeId, shift_id: shiftId, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  getEmployeeAttendanceSummary: async (employeeId: number, startDate: string, endDate: string) => {
    try { const r = await apiClient.get(`/attendance/attendance.php?action=employee_summary&employee_id=${employeeId}&start_date=${startDate}&end_date=${endDate}&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  generateAttendanceKPI: async (employeeId: number, startDate: string, endDate: string) => {
    try { const r = await apiClient.post(`/attendance/attendance.php?action=generate_attendance_kpi&tenant_id=${apiService.getTenantId()}`, { employee_id: employeeId, start_date: startDate, end_date: endDate, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  importDevicePunches: async (punches: any[], matchBy: string, source: string = 'biometric', importReference?: string) => {
    try { const r = await apiClient.post(`/attendance/attendance.php?action=import_device_punches&tenant_id=${apiService.getTenantId()}`, { punches, match_by: matchBy, source, import_reference: importReference, tenant_id: apiService.getTenantId() }); return r.data; } catch (e: any) { return { success: false, error: e?.response?.data?.error || 'Import failed' }; }
  },
  getDeviceKey: async () => {
    try { const r = await apiClient.get(`/attendance/attendance.php?action=device_key&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  regenerateDeviceKey: async (matchBy?: string) => {
    try { const r = await apiClient.post(`/attendance/attendance.php?action=device_key&tenant_id=${apiService.getTenantId()}`, { regenerate: true, match_by: matchBy, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  setDevicePushMatch: async (matchBy: string) => {
    try { const r = await apiClient.post(`/attendance/attendance.php?action=device_key&tenant_id=${apiService.getTenantId()}`, { match_by: matchBy, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  getBiometricMap: async () => {
    try { const r = await apiClient.get(`/attendance/attendance.php?action=biometric_map&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  setEmployeeBiometricId: async (employeeId: number, biometricId: string) => {
    try { const r = await apiClient.post(`/attendance/attendance.php?action=set_biometric_id&tenant_id=${apiService.getTenantId()}`, { employee_id: employeeId, biometric_id: biometricId, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },

  // ── Org Chart ─────────────────────────────────────────────────────────────
  getOrgChart: async () => {
    try { const r = await apiClient.get(`/org/org.php?tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  updateOrgStructure: async (data: any) => {
    try { const r = await apiClient.post(`/org/org.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },

  // ── Recruitment Extras ────────────────────────────────────────────────────
  getOfferLetters: async () => {
    try { const r = await apiClient.get(`/recruitment/recruitment_extras.php?type=offer&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  createOfferLetter: async (data: any) => {
    try { const r = await apiClient.post(`/recruitment/recruitment_extras.php?type=offer&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  updateOfferLetter: async (data: any) => {
    try { const r = await apiClient.put(`/recruitment/recruitment_extras.php?type=offer&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  getCommTemplates: async (templateType?: string) => {
    try { const r = await apiClient.get(`/recruitment/recruitment_extras.php?type=template&tenant_id=${apiService.getTenantId()}${templateType?'&template_type='+templateType:''}`); return r.data; } catch { return { success: false }; }
  },
  createCommTemplate: async (data: any) => {
    try { const r = await apiClient.post(`/recruitment/recruitment_extras.php?type=template&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  getBackgroundChecks: async () => {
    try { const r = await apiClient.get(`/recruitment/recruitment_extras.php?type=background&tenant_id=${apiService.getTenantId()}`); return r.data; } catch { return { success: false }; }
  },
  createBackgroundCheck: async (data: any) => {
    try { const r = await apiClient.post(`/recruitment/recruitment_extras.php?type=background&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  updateBackgroundCheck: async (data: any) => {
    try { const r = await apiClient.put(`/recruitment/recruitment_extras.php?type=background&tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },

  // ── Compensation → Payroll ────────────────────────────────────────────────
  assignGradeToEmployee: async (employeeId: number, gradeCode: string, salaryPoint: string = 'mid') => {
    try {
      const r = await apiClient.put(`/compensation/compensation.php?type=assign_grade&tenant_id=${apiService.getTenantId()}`, {
        employee_id: employeeId, grade_code: gradeCode, salary_point: salaryPoint, tenant_id: apiService.getTenantId()
      });
      return r.data;
    } catch { return { success: false, error: 'Failed' }; }
  },

  // ── Application Pipeline ──────────────────────────────────────────────────
  createApplication: async (data: any) => {
    try { const r = await apiClient.post(`/recruitment/applications.php?tenant_id=${apiService.getTenantId()}`, { ...data, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false, error: 'Failed' }; }
  },
  deleteApplication: async (id: number) => {
    try { const r = await apiClient.delete(`/recruitment/applications.php?tenant_id=${apiService.getTenantId()}`, { data: { id, tenant_id: apiService.getTenantId() } }); return r.data; } catch { return { success: false }; }
  },

  // ── Offer Letter Email ────────────────────────────────────────────────────
  sendOfferLetterEmail: async (id: number) => {
    try { const r = await apiClient.put(`/recruitment/recruitment_extras.php?type=offer&tenant_id=${apiService.getTenantId()}`, { id, action: 'send_email', tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },

  // ── Template Send ─────────────────────────────────────────────────────────
  sendTemplate: async (id: number, recipients: any[], variables: Record<string,string>) => {
    try { const r = await apiClient.put(`/recruitment/recruitment_extras.php?type=template&tenant_id=${apiService.getTenantId()}`, { id, action: 'send', recipients, variables, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  },
  deleteCommTemplate: async (id: number) => {
    try { const r = await apiClient.delete(`/recruitment/recruitment_extras.php?type=template&tenant_id=${apiService.getTenantId()}`, { data: { id, tenant_id: apiService.getTenantId() } }); return r.data; } catch { return { success: false }; }
  },

  // ── Background Check Report Email ─────────────────────────────────────────
  emailBackgroundCheckReport: async (recipients: string, subject: string, message: string, checkIds: number[]) => {
    try { const r = await apiClient.post(`/recruitment/recruitment_extras.php?type=background&tenant_id=${apiService.getTenantId()}`, { action: 'email_report', recipients, subject, message, check_ids: checkIds, tenant_id: apiService.getTenantId() }); return r.data; } catch { return { success: false }; }
  }
};
export default apiService;
